import React, { useState } from 'react';
import { Plus, Search, Edit2, Trash2, X, Mail, Phone, Lock, Eye } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { sendClientEmail } from '../../services/backendActions';
import './AdminPages.css';

const emptyForm = { name:'', contact:'', email:'', phone:'', industry:'', address:'', status:'Active' };

export default function ClientsPage() {
  const { data, commitData, showToast } = useApp();
  const { can, isFounder, isFinance } = useAuth();

  const canView   = can('canViewClients');
  const canAdd    = can('canAddClient');
  const canEdit   = can('canEditClient');
  const canDelete = can('canDeleteClient');

  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showView, setShowView]   = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [selected, setSelected]   = useState(null);
  const [form, setForm]           = useState(emptyForm);
  const [emailClient, setEmailClient] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  if (!canView) {
    return (
      <div className="admin-page">
        <div className="page-header"><div><h1 className="page-title">Clients</h1></div></div>
        <div className="perm-denied">
          <div className="perm-denied-icon"><Lock size={26} style={{ color:'var(--text-dim)' }}/></div>
          <p style={{ fontWeight:700 }}>Access Restricted</p>
          <p style={{ color:'var(--text-muted)',fontSize:'0.875rem' }}>Client data is restricted to Admins, Finance, Mentors, and Founders.</p>
        </div>
      </div>
    );
  }

  const filtered = data.clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.contact.toLowerCase().includes(search.toLowerCase())
  );

  const save = async () => {
    if (!form.name) { showToast('Company name required', 'error'); return; }
    try {
      if (editTarget) {
        await commitData('clients', data.clients.map(c => c.id === editTarget.id ? { ...c, ...form } : c));
        showToast('Client updated');
      } else {
        await commitData('clients', [...data.clients, { id:String(Date.now()), ...form, projects:0, revenue:0 }]);
        showToast('Client added');
      }
      setShowModal(false); setEditTarget(null); setForm(emptyForm);
    } catch {}
  };

  const openEdit = (c) => { setEditTarget(c); setForm({ name:c.name,contact:c.contact,email:c.email,phone:c.phone,industry:c.industry||'',address:c.address||'',status:c.status }); setShowModal(true); };

  const deleteClient = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try {
      await commitData('clients', data.clients.filter(cl => cl.id !== c.id));
      showToast('Client removed', 'warning');
    } catch {}
  };

  const totalRevenue = data.clients.reduce((s,c)=>s+c.revenue,0);
  const canSeeRevenue = isFounder || isFinance;

  const openClientEmail = (c) => {
    setEmailClient(c);
    setEmailSubject(`Message from Fortitude IT — ${c.name}`);
    setEmailBody('');
  };

  const sendClientMessage = async () => {
    if (!emailClient?.id || !emailBody.trim()) {
      showToast('Enter a message to send', 'error');
      return;
    }
    setEmailSending(true);
    try {
      await sendClientEmail({
        clientId: String(emailClient.id),
        subject: emailSubject.trim() || undefined,
        text: emailBody.trim(),
      });
      showToast(`Email sent to ${emailClient.email}`);
      setEmailClient(null);
      setEmailBody('');
    } catch (err) {
      showToast(err.message || 'Could not send email', 'error');
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Clients</h1>
          <p className="page-subtitle">{data.clients.length} clients · {canSeeRevenue ? `R ${totalRevenue.toLocaleString()} total revenue` : 'Revenue visible to founders and finance only'}</p>
        </div>
        <div className="page-actions">
          {canAdd && <button className="btn-gold" onClick={()=>{setEditTarget(null);setForm(emptyForm);setShowModal(true)}}><Plus size={15}/> Add Client</button>}
        </div>
      </div>

      <div className="filter-bar">
        <div className="search-box">
          <Search size={15}/>
          <input className="search-input" placeholder="Search clients..." value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead><tr><th>Client</th><th>Contact</th><th>Projects</th><th>Revenue</th><th>Industry</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {filtered.map(c=>(
              <tr key={c.id}>
                <td>
                  <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                    <div className="av av-md av-gold">{c.name.split(' ').slice(0,2).map(w=>w[0]).join('')}</div>
                    <div><p style={{ fontWeight:700 }}>{c.name}</p><p style={{ fontSize:'0.75rem',color:'var(--text-muted)' }}>{c.email}</p></div>
                  </div>
                </td>
                <td>
                  <p style={{ fontSize:'0.875rem',fontWeight:600 }}>{c.contact}</p>
                  <p style={{ fontSize:'0.75rem',color:'var(--text-muted)',display:'flex',alignItems:'center',gap:4 }}><Phone size={11}/>{c.phone}</p>
                </td>
                <td style={{ fontFamily:'var(--font-mono)',fontWeight:700 }}>{c.projects}</td>
                <td><span style={{ fontFamily:'var(--font-mono)',fontWeight:700,color:'var(--secondary-color)' }}>{canSeeRevenue ? `R ${c.revenue.toLocaleString()}` : 'Restricted'}</span></td>
                <td style={{ fontSize:'0.82rem',color:'var(--text-muted)' }}>{c.industry||'—'}</td>
                <td><span className={`badge ${c.status==='Active'?'badge-success':'badge-gray'}`}>{c.status}</span></td>
                <td>
                  <div style={{ display:'flex',gap:6 }}>
                    <button className="btn-ghost" style={{ padding:'5px 8px',fontSize:'0.72rem' }} onClick={()=>{setSelected(c);setShowView(true)}}><Eye size={12}/></button>
                    <button className="btn-ghost" style={{ padding:'5px 8px',fontSize:'0.72rem' }} onClick={()=>openClientEmail(c)} disabled={!c.email} title={c.email ? 'Send email' : 'No email on file'}><Mail size={12}/></button>
                    {canEdit   && <button className="btn-ghost" style={{ padding:'5px 8px',fontSize:'0.72rem' }} onClick={()=>openEdit(c)}><Edit2 size={12}/></button>}
                    {canDelete && <button className="btn-ghost" style={{ padding:'5px 8px',fontSize:'0.72rem',color:'var(--danger)' }} onClick={()=>deleteClient(c)}><Trash2 size={12}/></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* VIEW MODAL */}
      {showView && selected && (
        <div className="modal-overlay" onClick={()=>setShowView(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <p className="modal-title">{selected.name}</p>
              <button className="modal-close" onClick={()=>setShowView(false)}><X size={18}/></button>
            </div>
            <div className="detail-grid">
              <div className="detail-item"><span className="detail-label">Contact</span><span className="detail-value">{selected.contact}</span></div>
              <div className="detail-item"><span className="detail-label">Status</span><span className={`badge ${selected.status==='Active'?'badge-success':'badge-gray'}`}>{selected.status}</span></div>
              <div className="detail-item"><span className="detail-label">Email</span><span className="detail-value" style={{ fontSize:'0.8rem' }}>{selected.email}</span></div>
              <div className="detail-item"><span className="detail-label">Phone</span><span className="detail-value">{selected.phone}</span></div>
              <div className="detail-item"><span className="detail-label">Industry</span><span className="detail-value">{selected.industry||'—'}</span></div>
              <div className="detail-item"><span className="detail-label">Revenue</span><span className="detail-value" style={{ color:'var(--secondary-color)' }}>{canSeeRevenue ? `R ${selected.revenue.toLocaleString()}` : 'Restricted'}</span></div>
            </div>
            <p style={{ fontSize:'0.72rem',fontWeight:700,color:'var(--text-dim)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8 }}>Projects</p>
            {data.projects.filter(p=>p.client===selected.name||p.client===selected.name.split(' ')[0]).map(p=>(
              <div key={p.id} style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'7px 0',borderBottom:'1px solid var(--border-color)' }}>
                <span style={{ fontSize:'0.82rem',fontWeight:600 }}>{p.name}</span>
                <span className={`badge ${p.status==='Completed'?'badge-success':p.status==='In Progress'?'badge-info':'badge-gray'}`}>{p.status}</span>
              </div>
            ))}
            <div style={{ display:'flex',gap:10,justifyContent:'flex-end',marginTop:20 }}>
              {canEdit && <button className="btn-gold" onClick={()=>{setShowView(false);openEdit(selected)}}>Edit Client</button>}
            </div>
          </div>
        </div>
      )}

      {/* ADD / EDIT MODAL */}
      {showModal && (
        <div className="modal-overlay" onClick={()=>setShowModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <p className="modal-title">{editTarget?'Edit Client':'Add Client'}</p>
              <button className="modal-close" onClick={()=>setShowModal(false)}><X size={18}/></button>
            </div>
            <div className="modal-form">
              <div className="form-group"><label className="form-label">Company Name *</label><input className="form-input" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Company name"/></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Contact Person</label><input className="form-input" value={form.contact} onChange={e=>setForm({...form,contact:e.target.value})} placeholder="Full name"/></div>
                <div className="form-group"><label className="form-label">Industry</label><input className="form-input" value={form.industry} onChange={e=>setForm({...form,industry:e.target.value})} placeholder="E.g. Finance"/></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="contact@company.co.za"/></div>
                <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} placeholder="+27 11 000 0000"/></div>
              </div>
              <div className="form-group"><label className="form-label">Address</label><input className="form-input" value={form.address} onChange={e=>setForm({...form,address:e.target.value})} placeholder="City, Province"/></div>
              <div className="form-group"><label className="form-label">Status</label>
                <select className="form-input" value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>
                  <option>Active</option><option>Inactive</option>
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={()=>setShowModal(false)}>Cancel</button>
              <button className="btn-gold" onClick={save}>{editTarget?'Save Changes':'Add Client'}</button>
            </div>
          </div>
        </div>
      )}

      {emailClient && (
        <div className="modal-overlay" onClick={() => !emailSending && setEmailClient(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <p className="modal-title">Email {emailClient.name}</p>
              <button type="button" className="modal-close" onClick={() => setEmailClient(null)} disabled={emailSending}><X size={18}/></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 12 }}>To: {emailClient.email}</p>
              <div className="form-group">
                <label className="form-label">Subject</label>
                <input className="form-input" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Message</label>
                <textarea className="form-input" rows={5} value={emailBody} onChange={(e) => setEmailBody(e.target.value)} placeholder="Write your message…" />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-ghost" onClick={() => setEmailClient(null)} disabled={emailSending}>Cancel</button>
              <button type="button" className="btn-gold" onClick={sendClientMessage} disabled={emailSending}>
                {emailSending ? 'Sending…' : 'Send email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
